import { AfterViewInit, Component, OnInit, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { FeedsService } from 'src/app/shared/services/feeds/feeds.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { LoginService } from 'src/app/shared/services/login/login.service';
import { LtmsEventHandlerService } from 'src/app/shared/services/ltms-event-handler/ltms-event-handler.service';
import { SessionMgmtService } from 'src/app/shared/services/session-mgmt/session-mgmt.service';
import { TextAnalyticsService } from 'src/app/shared/services/text-analytics/text-analytics.service';
import { extendedRaterFile } from "src/app/shared/services/external-ts-files/extended-rating-mapper";
import * as wordcloud from 'wordcloud';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as d3 from 'd3';
import * as cloud from 'd3-cloud';
import { NgIf } from '@angular/common';
import { LtmsLoaderComponent } from '../../../../shared/components/ltms-loader/ltms-loader.component';
import { LtmsErrorComponent } from '../../../../shared/components/ltms-error/ltms-error.component';
import { LtmsDataTableV2Component } from '../../../../shared/components/ltms-data-table-v2/ltms-data-table-v2.component';
import { FeedDetailPanelComponent } from '../../../../shared/components/feed-detail-panel/feed-detail-panel.component';

@Component({
    selector: 'app-wordcloud',
    templateUrl: './wordcloud.component.html',
    styleUrl: './wordcloud.component.scss',
    imports: [NgIf, LtmsLoaderComponent, LtmsErrorComponent, LtmsDataTableV2Component, FeedDetailPanelComponent]
})
export class WordcloudComponent implements OnInit, AfterViewInit {
  tabDescription: string = 'Visualize key themes in customer feedback through word size, where larger words represent more frequent mentions, allowing for quick identification of top customer concerns and sentiments.'
  @Input() wordType: any = {};
  @Input() raterTypeObj: any = {};
  @Input() question: any = {};
  words: any = []
  univeralFilterMap: any = {
    "status": "action_filter.status_id_list",
    "category": "action_filter.category_id_list",
    "assignee": "action_filter.assigned_to_id_list",
    "tag": "action_filter.tag_id_list",
    "tat_status_closed": "action_filter.tat_status_list"
  }
  brandFilterMap: any = {
    "status": {
      key: "status_list",
      uniqueField: "uuid"
    },
    "category": {
      key: "category_list",
      uniqueField: "uuid"
    },
    "tag": {
      key: "tag_list",
      uniqueField: "uuid"
    }
  }
  addedValueOnFontSize: number = 14;
  destroyRef: any = inject(DestroyRef);
  columns: any = []
  feeds: any = []
  isFeedListLoaded: boolean = false;
  feedIdList: any = []
  filters: any = {}
  brandDetails: any = {}
  fromDate: any = null
  toDate: any = null;
  accountDetails: any = {}
  loggedManagerAccess: any = {}
  loggedManagerId: string = '';
  colorMap: any = {}
  selectedProjectType: any = {};
  isAccountLevelFeeds: boolean = false
  brandsMapping: any = {};
  brandIds: any = [];
  feedDetailsView: any = {
    open: false,
    isNext: false,
  };
  dateFilterType: number = 1
  limit: number = 10;
  pagingDetails: any = {
    start: 0,
    end: 0,
    pageSize: 10,
    pageNumber: 1,
    total: 0,
    totalpages: 1
  }
  pagingButtons: any = {
    isNext: false,
    isPrevious: false,
    isLast: false,
    isFirst: false
  }
  offsetMapping: any = {};
  wordcloudNotAvailable: boolean = false;
  loadingWordCloud: boolean = true;
  selectedWordCloudType = {
    label: 'By Words',
    value: 1
  }

  tableConfig: any = {
    isRowClickable: true,
    isExternalSort: true
  }
  mainRaterType: any = null
  drill_keywords: any = [];
  totalComments: any = []
  selectedQuestion: any = {}

  primaryQuestion: any = {};
  extenderRatingMapper = new extendedRaterFile();

  constructor(
    private _feedService: FeedsService,
    private _helpers: HelpersService,
    private _ltmsEvent: LtmsEventHandlerService,
    private _loginService: LoginService,
    private _session: SessionMgmtService,
    private _accountService: AccountsService,
    private _textAnalytics: TextAnalyticsService
  ) { }

  ngOnInit(): void {
    this._helpers.appState.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((state: any) => {
      if (state && !state.pending) {
        this.fromDate = state.datePicker.startDate;
        this.toDate = state.datePicker.endDate;
        this.brandDetails = state.brandDetails;
        this.accountDetails = state.accountDetails;
        this.pagingDetails = {
          start: 0,
          end: 0,
          pageSize: 10,
          pageNumber: 1,
          total: 0,
          totalpages: 1
        }
        this.pagingButtons = {
          isNext: false,
          isPrevious: false,
          isLast: false,
          isFirst: false
        }
        this.filters["pageNumber"] = 1;
        this.filters["dataSortOrder"] = 'desc';

        if (state.brandDetails || state.accountDetails) {
          this.preparePreloadData();
        }
      }
    })

  }
  ngAfterViewInit(): void {
  }

  preparePreloadData() {
    this.brandDetails = (!_.isEmpty(this.brandDetails)) ? this.brandDetails : Helpers.brandDetails;
    this.accountDetails = (!_.isEmpty(this.accountDetails)) ? this.accountDetails : Helpers.accountDetails;
    this.colorMap = {};
    this.setManagerDetails();
    this.columns = this.getColumns()
    if (this.isAccountLevelFeeds && !_.isEmpty(this.accountDetails)) {
      this.setBrandDetailsForAccount(this.accountDetails._id)
      if (this.raterTypeObj && this.raterTypeObj.value && this.raterTypeObj.value != this.mainRaterType) {
        this.mainRaterType = this.raterTypeObj.value;
        this.getWordCloud();
      } else if (this.wordType && this.wordType.value && this.selectedWordCloudType.value != this.wordType.value) {
        this.getWordCloud();
      }
      this.getAccountFeedList()
    }

    if (!this.isAccountLevelFeeds && !_.isEmpty(this.brandDetails)) {
      this.brandsMapping[this.brandDetails._id] = this.brandDetails;
      this.offsetMapping[this.brandDetails._id] = this._helpers.getBrandTimezoneDetails(this.brandDetails);
      this.selectedWordCloudType = { label: 'By words', value: 1 };
      if (_.get(this, 'brandDetails.detailed_feedback_items') && !_.isEmpty(this.brandDetails.detailed_feedback_items)) {
        this.colorMap[this.brandDetails._id] = this.getColorMap(this.brandDetails.detailed_feedback_items)
      }
      this.mainRaterType = this.brandDetails.main_rater_type;
      this.selectedQuestion = this.question;
      this.getWordCloud();
      this.getFeedList();

    }
  }
  setManagerDetails() {
    const brandId = (!_.isEmpty(this.brandDetails)) ? this.brandDetails._id : null;
    this.loggedManagerAccess = {}
    this.loggedManagerId = _.get(Helpers, 'managerDetails.manager._id', '');
    const accessList: any = _.get(Helpers, 'managerDetails.manager.manager_access', []);
    let access: any = null;
    if (brandId) {
      access = _.find(accessList, (access: any) => {
        return (access.brand_id == brandId && access.is_default_access)
      })
      if (_.isEmpty(access)) {
        const list = _.filter(accessList, (access: any) => {
          return (access.brand_id == brandId)
        })
        if (list.length) {
          access = list[0]
        }
      }
    }
    if (!_.isEmpty(access)) {
      const level: any = (_.get(access, 'access_key') && !_.isEmpty(access.access_key)) ? _.keys(access.access_key)[0] : null;
      if (level) {
        this.loggedManagerAccess = {
          "level": level,
          "value": (access.access_key[level] && access.access_key[level].values && access.access_key[level].values.length) ? access.access_key[level].values[0] : ""
        }
      }
    }

  }

  setBrandDetailsForAccount(accountId: string) {
    const brands = _.get(Helpers, 'brandListDetail', []);
    const brandcheck = brands.filter((ele: any) => { if (ele && ele['account_id'] && ele['account_id'] == accountId) { return ele; } })
    if (brandcheck && brandcheck.length > 0) {
      this.brandsMapping = {};
      this.brandIds = [];
      _.each(brands, (brand: any) => {
        if (brand && brand.project_info && brand.project_info.project_status && (brand.project_info.project_status.toString() == "1" || brand.project_info.project_status.toString() == "2")) {
          this.colorMap[brand._id] = this.getColorMap(brand.detailed_feedback_items)
          this.brandsMapping[brand._id] = brand;
          this.brandIds.push(brand._id)
        }
      });
    } else {
      this._accountService.getBrandsList(accountId).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((response: any) => {
        if (response.code == 0) {
          const brands = response.data.brands || [];
          this.brandsMapping = {};
          this.brandIds = [];
          _.each(brands, (brand: any) => {
            if (brand && brand.project_info && brand.project_info.project_status && (brand.project_info.project_status.toString() == "1" || brand.project_info.project_status.toString() == "2")) {
              this.colorMap[brand._id] = this.getColorMap(brand.detailed_feedback_items)
              this.brandsMapping[brand._id] = brand;
              this.brandIds.push(brand._id)
            }
          })
        }
      });
    }
  }

  getAccountFeedList() {
    this.isFeedListLoaded = false;
    this.feedIdList = [];
    // this.columns = this.getColumns()
    var filters = this.getFiltersForAccount(this.filters);
    var searchType = _.get(this.filters, "searchType");
    var searchText = _.get(this.filters, "searchText");
    var apiParams: any = {
      account_id: this.accountDetails._id,
      from: Helpers.datePicker.startDate,
      to: Helpers.datePicker.endDate,
      assigned_to_id_list: (!_.isEmpty(this.filters.assignedToIds)) ? _.get(this, "filters.assignedToIds") : null,
      followers_id_list: (!_.isEmpty(this.filters.assignedToIds)) ? _.get(this, "filters.followerIds") : null,
      data_source: 2,
      date_filter_type: 2,
      date_sort_type: "response_desc",
      page_number: this.pagingDetails.pageNumber,
      project_type: 1,
      brandIdList: (searchType && searchText) ? this.brandIds : null,
      filters: {
        assigned_to_id_list: (!_.isEmpty(this.filters.assignedToIds)) ? _.get(this, "filters.assignedToIds") : null,
        followers_id_list: (!_.isEmpty(this.filters.assignedToIds)) ? _.get(this, "filters.followerIds") : null,
        assignee_ids: [],
        date_filter_type: 2,
        date_sort_type: "response_desc",
        interaction_status: _.get(this.filters, "interactionStatus", null)
      }
    }
    if (this.selectedProjectType && this.selectedProjectType.type) {
      apiParams.project_type = this.selectedProjectType.type
    }
    this._feedService.getAccontFeedList(apiParams).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      if (response.code == 0 && _.get(response, 'data.feed') && !_.isEmpty(response.data.feed)) {
        this.processFeedData(response.data);
      } else {
        this.feeds = []
        this.isFeedListLoaded = true;
      }
    })
  }

  getFiltersForAccount(filters) {
    var filterMap = {
      "date_sort_type": (filters.dataSortOrder && (filters.dataSortOrder.indexOf("asc") > -1)) ? "response_asc" : "response_desc",
      "date_sort_order": filters.dataSortOrder,
      "date_filter_type": filters.dateFilterType || 2,
      "assignee_ids": filters.assignedToIds,
      "followers_id_list": filters.followerIds,
      "interaction_status": filters.interactionStatus
    };
    return filterMap;
  }

  layout: any = null;
  renderWordCloud(data) {

    try {
      var maxSize = _.maxBy(this.words, 'count');
      var minSize = _.minBy(this.words, 'count');
      // Added as temperary solution because of bug in D3 library
      if (maxSize && minSize && maxSize.count == minSize.count) {
        this.words[0].count = this.words[0].count + 1;
      }
      const el = document.getElementById('litmus-word-cloud');
      const className = 'litmus-word-cloud';
      if (el) {
        el.innerHTML = '';
        this.layout = cloud()
          .size([el.offsetWidth, el.offsetHeight])
          .words(_.map(this.words, (d: any) => {
            d.text = d.words;
            d.size = this.getFontSize(maxSize.count, minSize.count, d.count)
            return d;
          }))
          .padding(5)
          .rotate(function () { return 0 })
          .fontSize((d: any) => { return d.size; })
          .spiral('archimedean')
          .on("end", this.draw.bind(this));

        this.layout.start();
      }
    } catch (exception) {
      console.error(exception)
    }
  }
  updateWindow() {
    const el = document.getElementById('litmus-word-cloud');
    if (el) {
      // this.renderWordCloud(this.words)
    }

  }
  draw(words: any) {
    try {

      d3.select(window).on('resize', this.updateWindow.bind(this));
      d3.select(".litmus-word-cloud")
        .append("svg")
        .attr("preserveAspectRatio", "xMinYMin meet")
        .attr("width", this.layout.size()[0])
        .attr("height", this.layout.size()[1])
        .classed("svg-content-responsive", true)
        .append("g")
        .attr("transform", "translate(" + this.layout.size()[0] / 2 + "," + this.layout.size()[1] / 2 + ")")
        .selectAll("text")
        .data(words)
        .enter().append("text")
        .style("font-size", function (d: any) {
          return d.size + "px";
        })
        .style("font-family", "Asap")
        .style("color", "Impact")
        .attr("text-anchor", "middle")
        .style("background", 'black')
        .style('cursor', 'pointer')
        .style("fill", (d: any, i) => {
          return d.color;
        })
        .attr("transform", function (d: any) {
          return "translate(" + [d.x, d.y] + ")rotate(" + d.rotate + ")";
        })
        .text(function (d: any) { return d.text; })
        .on("click", this.applyDrillDown.bind(this));
    } catch (exception) {
      console.error(exception)
    }

  }

  applyDrillDown(event: any, param: any) {
    this.drill_keywords.push(param.words);
    if (!this.isAccountLevelFeeds) {
      var data = {
        "value": param.words,
        "filter_sub_type": "keyword_search"
      }
      var question_id = this.selectedQuestion && this.selectedQuestion.field_id ? this.selectedQuestion.field_id : 'comments';
      Helpers.setUniversalFilter('text_filter', question_id, data);
    }
    // getKeyWords();
  }
  getFontSize(currMax, currMin, size) {
    if (size == currMin && currMin == currMax) {
      return 25 + this.addedValueOnFontSize;
    } else {
      return (((size - currMin) / (currMax - currMin)) * 25) + this.addedValueOnFontSize;
    }

  }
  renderTable() {
    this.getFeedList();
  }
  getFeedList() {
    this.isFeedListLoaded = false;
    this.feedIdList = []

    this.getParametersForAPI((params) => {
      this._feedService.getFeedsByFilter(params).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((response) => {
        if (response.code == 0 && _.get(response, 'data.feed') && !_.isEmpty(response.data.feed)) {
          this.processFeedData(response.data)
        } else {
          this.feeds = []
          this.isFeedListLoaded = true;
        }
      });
    })
  }

  getParametersForAPI(cb) {
    // this.filters
    var isRoleBasedProject = _.get(this.brandDetails, "action_management_configuration.isRoleBasedAssignee") || false;
    var roleAssignedTo = _.get(this, "filters.roleAssignedTo") ? _.get(this, "filters.roleAssignedTo") : [];
    var rolesFollowedBy = _.get(this, "filters.rolesFollowedBy") ? _.get(this, "filters.rolesFollowedBy") : [];
    var getFromEs = false;
    if (roleAssignedTo.length || rolesFollowedBy.length) {
      getFromEs = true;
    }
    var filters: any = {
      accountId: (_.isObject(this.accountDetails) && !_.isEmpty(this.accountDetails)) ? this.accountDetails._id : null,
      shopIds: Helpers.allTouchPointsSelected != false ? Helpers.shopIdList : null, // This is not getting used in the API
      primaryRatingLevels: [], // Array of primary rating to filter the feed by primary rating(Example: if primary_rating_levels have 1 and 2 then it return the feeds which have primary rating 1 or 2).
      statusIds: this.getFilter('status'), // Status id Array
      tatStatusClosed: this.getFilter('tat_status_closed'), // TAT Status for feeds
      categoryIds: this.getFilter('category'), // Category id Array
      tagIds: this.getFilter('tag'), // tag id Array
      /** Setting the below parameters assignedToIds and followerIds with null as those will be the part of global filters and will be in universalFilter*/
      assignedToIds: _.get(this, "filters.assignedToIds"), // Assignee Id Array
      followerIds: _.get(this, "filters.followerIds"),
      brandId: null,
      groupId: null,
      shopId: null,
      from: this.fromDate,
      to: this.toDate,
      limit: _.get(this, "filters.limit") || this.limit,
      isPublish: null, // Not in use. This was to show published feedbacks.
      commentKeyword: null, // Not in use. This was for filtering out feeds on basis of this keyword if comment_keyword = 'better' then it return those feed who have the 'better' keyword in comments or detailed_rating.comment(based on the comment_keyword_switch)
      commentKeywordSwitch: 1,//_.get(this, "filters.includePrimaryComments") || 1, // Filter through comment(primary, non-primary comment), 1: Primary Comment 2: Non Primary comment
      // emptyCommentsFilter: this.getCommentFilter(), // To filter either by empty or non-empty comment or both
      emptyCommentsFilter: 1, // wordcloud work with primary comment only so no need filter by empty or non-empty comment or both
      includeExcludedFeeds: this.getStatsFilter(), // To filter by the feeds which are excluded from stats
      dateFilterType: this.dateFilterType || _.get(this, "filters.dateFilterType") || 2, // sort date by request or response date. Default Sort through created_date
      dataSortOrder: _.get(this, "filters.dataSortOrder") || 'desc',
      userId: _.get(this, "filters.userId") || null, // Not used in feed count
      interactionStatus: _.get(this, "filters.interactionStatus"), // Not used in feed count
      fieldId: 'comments', // Not in use
      searchType: _.get(this, "filters.searchType") || null, // Not used in feed count
      searchText: _.get(this, "filters.searchText") || null, // Not used in feed count
      isFilterByHistory: null, // Not used in feed count
      customFilterList: this.getCustomHierarchyFilters(), // Not used in feed count
      hierarchyFilterDetailsList: (Helpers.hierarchyStack && Helpers.hierarchyStack.length) ? Helpers.hierarchyStack : [],
      textV2Status: true,
      // If ES enabled or textV2Status enabled then get data from ES
      dataSource: !(!(_.isEmpty(Helpers.universalFilter)) || _.get(this, "filters.textV2Status") || (getFromEs && isRoleBasedProject)) ? 1 : 2,
      universalFilter: this.getUniversalFilter(Helpers.universalFilter),
      is_service_management: _.get(this, "filters.is_service_management") || false, //flag to identify if the project is service management
      roleAssignedTo: roleAssignedTo,
      rolesFollowedBy: rolesFollowedBy
    }

    if (filters.tatStatusClosed && filters.tatStatusClosed.length > 0) {
      filters.consideredClosedStatus = _.get(Helpers, "brandDetails.lw_status_list_map.LW_CLOSED") || _.get(this, "brandDetails.lw_status_list_map.LW_CLOSED");
      filters.TAThours = _.get(Helpers, "brandDetails.action_management_configuration.ageing_config.turnaround_time") || _.get(this, "brandDetails.action_management_configuration.ageing_config.turnaround_time");

      var actionableArray = [];
      actionableArray = _.get(Helpers, "brandDetails.action_management_configuration.actionables") || _.get(this, "brandDetails.action_management_configuration.actionables");
      var finalActionableArray = [];
      if (actionableArray && actionableArray.length > 0) {

        finalActionableArray = this.getActionableRatings(actionableArray);
        var ageTypeForTAT = _.get(Helpers.brandDetails, "action_management_configuration.ageing_config.type");
        filters.age_type = ageTypeForTAT ? ageTypeForTAT : 1;
        filters.actionableRatings = finalActionableArray;
      }
    }

    if (_.get(this, "filters.pageNumber") > 1) {
      filters['pageNumber'] = _.get(this, "filters.pageNumber"); // Not used in feed count
    } else {
      filters['pageNumber'] = 1;
    }

    if (filters.searchText || filters.searchType) {
      filters.dataSource = 2;
    }
    if (Helpers.touchpointId) {
      filters["shopId"] = Helpers.touchpointId;
    } else if (_.get(this, "params.brandId")) {
      filters["brandId"] = _.get(this, "params.brandId")
    } else {
      filters["brandId"] = _.get(this, "brandDetails._id");
    }

    // Requirement Change: If assignedToIds or followerIds exists then pass brand id to assignee_follower_brand_id
    if (_.get(this, "filters.assignedToIds.length") || _.get(this, "filters.followerIds.length") || _.get(this, "filters.roleAssignedTo.length") || _.get(this, "filters.rolesFollowedBy.length")) {
      filters["assigneeFollowerBrandId"] = _.get(this, "brandDetails._id");
    }
    this.filters = filters
    cb(filters);
  }
  processFeedData(feeddata: any, details?: any) {
    this.feedIdList = [];
    const feedList = _.get(feeddata, 'feed', []);
    const managersMap = this.getHashMapbyKey(_.get(feeddata, 'managers', []), '_id');
    const shopsMap = this.getHashMapbyKey(_.get(feeddata, 'shops', []), '_id');
    const usersMap = this.getHashMapbyKey(_.get(feeddata, 'users', []), '_id');
    const brands = _.get(feeddata, 'brands', {})
    if (!_.isEmpty(brands)) {
      this.updateBrandMapping(brands);
    }

    const timestamp_sla_v3 = _.get(feeddata, 'timestamp_sla_v3');
    const total_feeds = _.get(feeddata, 'total_feeds');
    if (feedList.length) {
      this.feeds = _.map(feedList, (feed: any) => {
        var feedCreatedDate = Helpers.deepClone(feed["created_date"]);
        var isClosedListAvailable: boolean = false;
        this.feedIdList.push(feed._id)
        if (this.brandsMapping[feed.brand_id]) {
          if (this.brandsMapping[feed.brand_id].lw_status_list_map) {
            isClosedListAvailable = (this.brandsMapping[feed.brand_id].lw_status_list_map && this.brandsMapping[feed.brand_id].lw_status_list_map.LW_CLOSED) && (this.brandsMapping[feed.brand_id].lw_status_list_map.LW_CLOSED.length > 0);
          }

          let feedDetails: any = Helpers.deepClone(feed);
          let primaryRating = _.get(feed, 'feed_params.primary_rating', null)
          // Check if the question is of a specific component type and if extended rater type is 4
          if (this.primaryQuestion?.component_type == 'ltms-nps-five-star-rater' && Helpers.brandDetails?.extended_rater_type === 4 && (!_.isEmpty(Helpers.brandDetails.extended_rating_mapper))) {
            // Get the updated distribution using dynamic mapping logic
              primaryRating = this.extenderRatingMapper.getExtendedRaterMappingPrimaryRating(primaryRating, this.primaryQuestion);
          }
          let primaryRatingColor = _.get(this.colorMap, feed.brand_id + "." + _.get(feed, 'feed_params.primary_rating'), '#00000033');
          if (_.isNull(feed.feed_params.primary_rating)) {
            primaryRating = 'NA';
            primaryRatingColor = '#00000033'
          }
          feedDetails = _.assign(feedDetails, {
            primary_rating: primaryRating,
            comments: (_.get(feed, 'feed_params.comments')) ? _.get(feed, 'feed_params.comments') : '',
            created_date: this._helpers.getTimeByOffset(this.offsetMapping[feed.brand_id]["__zone_symbol__value"]['offset'], _.get(feed, 'created_date'), "DD MMM 'YY HH:mm"),//moment(_.get(feed, 'created_date')).format('DD MMM 'YY hh:mm'),
            created_date_timestamp: moment(_.get(feed, 'created_date')).valueOf(),
            last_updated_at: this._helpers.getTimeByOffset(this.offsetMapping[feed.brand_id]["__zone_symbol__value"]['offset'], _.get(feed, 'last_updated_at'), "DD MMM 'YY HH:mm"),//moment(_.get(feed, 'last_updated_at')).format('DD MMM 'YY hh:mm'),
            last_updated_at_timestamp: moment(_.get(feed, 'last_updated_at')).valueOf(),
            status_name: _.get(feed, 'status_details.0.name'),
            collapsableFields: ['comments'],
            due_date: this._helpers.getTimeByOffset(this.offsetMapping[feed.brand_id]["__zone_symbol__value"]['offset'], _.get(feed, 'finalans'), "DD MMM 'YY HH:mm"), //moment(_.get(feed, 'finalans')).format('DD MMM 'YY hh:mm'),
            due_date_timestamp: moment(_.get(feed, 'finalans')).valueOf(),
            user_name: _.get(feed, "feedback_request_data.tag_private_user_name") || _.get(usersMap, feed.feed_by_id + '.name') || 'Anonymous',
            shop_name: this.getTouchpointName(_.get(shopsMap, feed.feed_target_id + '.name', "NA"), feed.litmus_processed_data, this.brandsMapping[feed.brand_id]),
            project_name: _.get(this.brandsMapping, feed.brand_id + '.name', "NA"),
            coloMap: {
              primary_rating: primaryRatingColor,
              due_date: _.get(feed, 'finalanscolor')
            }
          })
          return feedDetails
        }

      })
    }
    this.updatePagingDetails(total_feeds, this.filters.pageNumber)
    if (this.feedDetailsView.open) {
      const feedId = (this.feedDetailsView.isNext) ? this.feedIdList[0] : this.feedIdList[this.feedIdList.length - 1];
      const data: any = {
        row: {
          _id: feedId
        }
      };
      this.showFeedDetails(data)
      this.feedDetailsView.open = false;
    }

    this.isFeedListLoaded = true;
  }
  getColumns() {
    const ratingColumn = {
      header: 'Rating',
      dataKey: 'primary_rating',
      dataType: "number",
      enabled: true,
      sortingEnabled: false,
      componentType: 'badges',
      style: {
        headerTextAlign: 'center',
        cellTextAlign: 'center',
        width: "10%"
      },
      order: 1
    }
    const responseColumn = {
      header: 'Feedback',
      dataKey: 'comments',
      dataType: "string",
      componentType: 'collapsable',
      ellipsisEnabled: true,
      sortingEnabled: false,
      maxChar: 20,
      enabled: true,
      clickable: true,
      descriptionFields: ["user_name", "shop_name", "feedback_reference_token"],
      style: {
        headerTextAlign: 'left',
        cellTextAlign: 'left',
        width: "60%"
      },
      order: 2,
    }
    const recievedColumn = {
      header: 'Received on',
      dataKey: 'created_date',
      dataType: "string",
      sortBy: 'created_date_timestamp',
      enabled: true,
      isColumnSorted: true,
      sorted: true,
      style: {
        headerTextAlign: 'left',
        cellTextAlign: 'left',
        width: "20%"
      }, 
      order: 3
    }

    return [ratingColumn, responseColumn, recievedColumn];
  }
  showFeedDetails(event: any) {
    this._ltmsEvent.emit('feed-detail-panel', {
      feed_id: event.row._id,
      feedIdList: (this.feedIdList.length) ? this.feedIdList : [],
      pagingDetails: this.pagingDetails
    })
  }

  onPageChange(event: any) {
    this.feedDetailsView.open = true;
    this.feedDetailsView.isNext = _.get(event, 'isNext', false)
    this.filters["pageNumber"] = _.get(event, 'pageNumber', 1)
    if (this.isAccountLevelFeeds) {
      this.getAccountFeedList();
    } else {
      this.getFeedList()
    }

  }
  sortFeedList(event: any) {
    if (event) {
      const sortOrder = (event.sorted) ? 'asc' : 'desc'
      this.columns = _.map(this.columns, (column: any) => {
        if (column.dataKey == event.dataKey) {
          column.isColumnSorted = true;
          column.sorted = event.sorted;
          column.sortingEnabled = true;
        } else {
          delete column.isColumnSorted;
          delete column.sorted;
        } 
        return column;
      })
      if (event.dataKey != 'created_date') {
        const field = (event.sortBy) ? event.sortBy : event.dataKey;
        this.feeds = _.orderBy(this.feeds, [field], [sortOrder]);
        this.feedIdList = _.map(this.feeds, (feed: any) => feed._id)
      } else {
        this.filters.dataSortOrder = sortOrder;
        if (this.isAccountLevelFeeds) {
          this.getAccountFeedList()
        } else {
          this.getFeedList();
        }
      }

    }
  }
  getFilter(type: string) {
    var list = [];
    var filterDetail = _.get(Helpers, "universalFilter." + this.univeralFilterMap[type]);
    // Every filter will must action(such as eq, ne) and value(list of uuid or id etc)
    if (_.get(filterDetail, "action") && _.get(filterDetail, "values.length")) {
      if (filterDetail["action"] == "eq") {
        list = filterDetail["values"];
      } else if (filterDetail["action"] == "ne") {
        // Filter out list element which are deleted and not disabled
        var elemInBrandDetail = _.filter(_.get(this.brandDetails, this.brandFilterMap[type]["key"]), (elem) => {
          if (elem["is_deleted"] != true && elem["is_enabled"] != false) {
            return elem;
          }
        })
        var listInBrand = _.map(elemInBrandDetail, this.brandFilterMap[type]["uniqueField"]);
        list = _.difference(listInBrand, filterDetail["values"]);
      }
    }
    return list;
  }
  getCommentFilter() {
    var filter = _.get(Helpers, "universalFilter.action_filter.comment.values");
    // Default has to be 1(include with and without comments), 2: with comment, 3: without comments
    return (filter) ? filter : 1;
  }
  getStatsFilter() {
    var shouldInclude = _.get(this.filters, "includeExcludedFeeds");
    return (shouldInclude == true || shouldInclude == false) ? shouldInclude : false;
  }
  getCustomHierarchyFilters() {
    var hierarchyFilters: any = [];
    // this.brandDetails = (this.brandDetails) ? this.brandDetails : Helpers.brandDetails;
    if (this.brandDetails && this.brandDetails.is_single_touchpoint_setup && Helpers.hierarchyStack && Helpers.hierarchyStack.length) {
      _.each(Helpers.hierarchyStack, function (hierarchy) {
        if (hierarchy.field_id && hierarchy.group_key && hierarchy.group_key.length) {
          hierarchyFilters.push({
            "field": hierarchy.field_id,
            "value": hierarchy.group_key[0]
          })
        }

      })
    }
    return hierarchyFilters;
  }

  getUniversalFilter(universalFilter) {
    var filter = universalFilter || {};
    if (!_.isEmpty(universalFilter) && !_.isEmpty(universalFilter["action_filter"])) {
      filter = Helpers.deepClone(universalFilter);
      _.each(filter["action_filter"], (value, key) => {
        delete value["details"];
        delete value["selected_values"];
        delete value["filterName"];
        // If this.filters contains assignedToIds then merge it universal filter's assignee list
        if (key == "assigned_to_id_list") {
          var assignToMeId = _.get(this, "filters.assignedToIds") || [];

          // Merging Universal filter assignee list with assign to me id
          value["values"] = (_.get(value, "values.length")) ? value["values"].concat(assignToMeId) : assignToMeId;
        }
        if(key == 'comment') {
          delete filter["action_filter"][key];
        }
      })
    }
    return filter;
  }
  getActionableRatings(actionableArray) {
    var currentDate = new Date();
    var actionablesLength = actionableArray.length;
    for (var actionableArrayCount = 0; actionableArrayCount < actionablesLength; actionableArrayCount++) {
      if (actionableArray[actionableArrayCount]["date"]["start"] && actionableArray[actionableArrayCount]["date"]["end"]) {
        var minDate = new Date(actionableArray[actionableArrayCount]["date"]["start"]);
        var maxDate = new Date(actionableArray[actionableArrayCount]["date"]["end"]);

        if (currentDate >= minDate && currentDate <= maxDate) {
          return actionableArray[actionableArrayCount]["primary_rating"];
        }
      } else if (actionableArray[actionableArrayCount]["date"]["start"] && !actionableArray[actionableArrayCount]["date"]["end"]) {
        var minDate = new Date(actionableArray[actionableArrayCount]["date"]["start"]);
        var maxDate = currentDate;

        if (currentDate >= minDate && currentDate <= maxDate) {
          return actionableArray[actionableArrayCount]["primary_rating"];
        }

      }
    }
  }

  paginateFeedList(pagingType: string) {
    if (pagingType == 'next') {
      this.filters["pageNumber"]++;
    } else if (pagingType == 'previous') {
      this.filters["pageNumber"]--;
    } else if (pagingType == 'first') {
      this.filters["pageNumber"] = 1;
    } else if (pagingType == 'last') {
      this.filters["pageNumber"] = this.pagingDetails.totalpages;
    }
    if (this.isAccountLevelFeeds) {
      this.getAccountFeedList();
    } else {
      this.getFeedList()
    }
  }

  getWordCloud() {
    this.wordcloudNotAvailable = false;
    this.loadingWordCloud = true;
    this.words = [];
    let params: any = {};
    if (this.wordType && this.wordType.value) {
      this.selectedWordCloudType = this.wordType;
    }

    if (this.isAccountLevelFeeds) {
      params['account_id'] = this.accountDetails._id;
      params['main_rater_type'] = this.mainRaterType;
      params['drill_keywords'] = this.drill_keywords;
    } else {
      params = this.getInitialQueryParameters();
    }
    params["field_id"] = 'comments';

    params['from'] = (this.fromDate) ? this.fromDate : Helpers.datePicker.startDate;
    params['to'] = (this.toDate) ? this.toDate : Helpers.datePicker.endDate;
    params['cloud_type'] = this.selectedWordCloudType.value;

    params['is_custom_date_range_selected'] = true;
    params['text_v2_status'] = true;

    if (Helpers.hierarchyStack && Helpers.hierarchyStack.length) {
      params.custom_filter_list = [];
      _.each(Helpers.hierarchyStack, function (hierarchy) {
        var obj = {};
        obj['field'] = hierarchy.field_id;
        obj['value'] = hierarchy.group_key[0];
        params.custom_filter_list.push(obj);
      });
    }
    this._textAnalytics.getKeyWords(params).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code == 0 && _.hasIn(response, 'data.aggregations.keywords.buckets') && !_.isEmpty(response.data.aggregations.keywords.buckets)) {
        this.words = this.prepareWordCloudData(response.data.aggregations.keywords.buckets);
        this.totalComments = this.words;
        if (!_.isEmpty(this.words)) {
          this.wordcloudNotAvailable = false;
          this.loadingWordCloud = false
          // this.renderWordCloud(this.words);
          setTimeout(() => {
            this.renderWordCloud(this.words);
          }, 500);
        } else {
          this.loadingWordCloud = false;
          this.wordcloudNotAvailable = true;
          this.words = [];
        }
      } else {
        this.loadingWordCloud = false;
        this.wordcloudNotAvailable = true;
        this.words = [];
      }

    })
  }

  prepareWordCloudData(buckets) {
    var data: any = [];
    _.each(buckets, (bucket) => {
      if (bucket['key'] && bucket['key'].length > 3) {
        var obj = {},
          cloudObj = {};
        _.each(bucket.range.buckets, (bucket) => {
          if (bucket.key == 'current') {
            if (this.mainRaterType == 3 || this.mainRaterType == 10) {
              obj['promoter'] = bucket.promoters.doc_count;
              obj['passive'] = bucket.passives.doc_count;
              obj['detractor'] = bucket.detractors.doc_count;
            } else if ([4, 6, 7, 9].indexOf(this.mainRaterType) != -1) {
              obj['top'] = bucket.top.doc_count;
              obj['mid'] = bucket.mid.doc_count;
              obj['bottom'] = bucket.bottom.doc_count;
            } else {
              obj['positive'] = bucket.positives.doc_count;
              obj['negative'] = bucket.negatives.doc_count;
            }
            obj['count'] = bucket.doc_count;
            obj['color'] = this.getColors(bucket);
          }
        });
        obj['words'] = bucket['key'];
        data.push(obj);
      }
    });
    // renderWordCloud(data);
    return data;
  }

  getColors(bucket) {
    var color = 'grey';
    if (this.mainRaterType == 3 || this.mainRaterType == 10) {
      var max = Math.max(bucket.promoters.doc_count, bucket.passives.doc_count, bucket.detractors.doc_count);
      if (max == bucket.promoters.doc_count) {
        color = '#008000';
      } else if (max == bucket.passives.doc_count) {
        color = '#ffa500';
      } else if (max == bucket.detractors.doc_count) {
        color = '#ff0000';
      }
    } else if ([4, 6, 7, 9].indexOf(this.mainRaterType) != -1) {
      var max = Math.max(bucket.top.doc_count, bucket.mid.doc_count, bucket.bottom.doc_count);
      if (max == bucket.top.doc_count) {
        color = '#10a754';
      } else if (max == bucket.mid.doc_count) {
        color = '#fab416';
      } else if (max == bucket.bottom.doc_count) {
        color = '#ed2424';
      }
    } else {
      var max = Math.max(bucket.positives.doc_count, bucket.negatives.doc_count);
      if (max == bucket.positives.doc_count) {
        color = '#0b7139';
      } else if (max == bucket.negatives.doc_count) {
        color = '#ed2424';
      }
    }
    return color;
  }

  updatePagingDetails(totalfeeds: number, pageNumber: number) {
    if (totalfeeds > this.pagingDetails.pageSize) {
      pageNumber = (pageNumber > 0) ? pageNumber : 0;
      let start = (pageNumber > 0) ? ((pageNumber - 1) * this.pagingDetails.pageSize) + 1 : 0;
      let end = (pageNumber > 0) ? (pageNumber * this.pagingDetails.pageSize) : 0;
      end = (pageNumber >= this.pagingDetails.totalpages) ? ((pageNumber - 1) * this.pagingDetails.pageSize) + this.feeds.length : end;
      this.pagingDetails = {
        start: start,
        end: end,
        pageSize: 10,
        pageNumber: pageNumber,
        total: totalfeeds,
        totalpages: Math.ceil(totalfeeds / 10)
      }
    } else {
      this.pagingDetails = {
        start: (totalfeeds > 0) ? 1 : 0,
        end: (totalfeeds > 0) ? totalfeeds : 0,
        pageSize: 10,
        pageNumber: 1,
        total: (totalfeeds > 0) ? totalfeeds : 0,
        totalpages: 1
      }
    }
    this.pagingDetails.total = totalfeeds;
    this.pagingButtons.isNext = false;
    this.pagingButtons.isFirst = false;
    this.pagingButtons.isPrevious = false;
    this.pagingButtons.isLast = false;

    if (this.pagingDetails.pageNumber < this.pagingDetails.totalpages) {
      this.pagingButtons.isNext = true;
      this.pagingButtons.isLast = true;
    }

    if (this.pagingDetails.pageNumber > 1) {
      this.pagingButtons.isFirst = true;
      this.pagingButtons.isPrevious = true;
    }

    if (this.pagingDetails.total < this.pagingDetails.pageSize) {
      this.pagingButtons.isNext = false;
      this.pagingButtons.isFirst = false;
      this.pagingButtons.isPrevious = false;
      this.pagingButtons.isLast = false;
    }
  }

  getTouchpointName(defaultName, tags, brand) {
    if (brand && brand.is_single_touchpoint_setup == true) {
      var levels: any = [];
      try {
        var maxLevel = 0;
        var maxLevelValue = defaultName;
        for (var tag in tags) {
          if (tag.indexOf('tag_private_group_h_') >= 0 && tags[tag]) {
            var currentLevel = parseInt((tag.replace('tag_private_group_h_', "").replace('_', "")));
            var levelValue = (tags[tag] instanceof Array) ? tags[tag][0] : tags[tag];
            levels.push({
              "value": levelValue,
              "level": currentLevel
            })
            if ((currentLevel > maxLevel) && levelValue) {
              maxLevelValue = (tags[tag] instanceof Array) ? tags[tag][0] : tags[tag];
              maxLevel = currentLevel;
            }
          }
        }
        return _.max(_.filter(levels, function (lvl) { return lvl.level != maxLevel }), "level").value;

      } catch (e) {
        return defaultName;
      }

    } else {
      return defaultName;
    }
    return defaultName;
  }

  getColorMap(detailedFeedbackItems: any) {
    let mapping = {};
    if (!_.isEmpty(detailedFeedbackItems)) {
      const primaryRatingQuestion = _.find(detailedFeedbackItems, (question: any) => {
        return question.field_id == "primary_rating";
      })
      if (primaryRatingQuestion && primaryRatingQuestion.prop) {
        this.primaryQuestion = primaryRatingQuestion;
        _.each(primaryRatingQuestion.prop, (detail: any, key: string) => {
          mapping[key] = detail.active_color
        })
      }
    }
    return mapping;
  }

  getHashMapbyKey(list: any, key: string) {
    let map: any = {};
    if (!_.isEmpty(list)) {
      _.each(list, (item: any) => {
        map[item[key]] = item;
      })
    }
    return map;
  }

  updateBrandMapping(brands: any) {
    this.brandsMapping = {}
    this.colorMap = {}
    if (!_.isEmpty(brands)) {
      _.each(brands, (brand: any) => {
        this.brandsMapping[brand._id] = brand;
        this.colorMap[brand._id] = this.getColorMap(brand.detailed_feedback_items)
        this.offsetMapping[brand._id] = this._helpers.getBrandTimezoneDetails(brand)
      })
    }
  }

  getAllStatusForHide(statusArray: any) {
    var counttemp: number = 0;
    var arraylength: number = statusArray.length;
    var allhiddenStatuses: any = []
    while (counttemp < arraylength) {
      var statusTemp = statusArray[counttemp]["hide_slaTimer_for_status"];
      var hideStatus = (statusTemp == true || statusTemp == false) ? statusTemp : null;

      if ((!(hideStatus == null)) && (hideStatus == true)) {
        allhiddenStatuses.push(statusArray[counttemp]["uuid"]);
      }
      counttemp++;
    }

    return allhiddenStatuses;
  }

  getInitialQueryParameters() {

    var parameter = Helpers.deepClone(Helpers.accessObject);
    var initialQueryParams: any = {};

    if (!_.isEmpty(parameter)) {
      initialQueryParams = {
        "manager_type": parameter.manager_type,
        "brand_id": parameter.brand_id
      }
      if (parameter.manager_type == 1 && parameter.shop_id) {
        initialQueryParams.shop_id = Helpers.touchpointId;
        initialQueryParams.main_rater_type = this.brandDetails.main_rater_type;
      } else if (parameter.manager_type == 7 && parameter.group_id) {
        initialQueryParams.group_id = parameter.group_id;
        initialQueryParams.main_rater_type = this.brandDetails.main_rater_type;
      } else if (parameter.manager_type == 2 && parameter.brand_id) {
        initialQueryParams.brand_id = parameter.brand_id;
        initialQueryParams.main_rater_type = this.brandDetails.main_rater_type;
      }

      if (Helpers.touchpointId) {
        initialQueryParams.shop_id = Helpers.touchpointId;
      } else if (Helpers.groupId) {
        initialQueryParams.group_id = Helpers.groupId;
      }

      if (!Helpers.touchpointId) {
        initialQueryParams.shop_id_list = Helpers.shopIdList;
      }

      if (Helpers.hierarchyStack && Helpers.hierarchyStack.length) {
        initialQueryParams.hierarchy_stack = Helpers.hierarchyStack;
      }

      if (Helpers.customFilterNextLevel && _.get(this.brandDetails, "is_single_touchpoint_setup")) {
        initialQueryParams.group_alias_key = Helpers.customFilterNextLevel;
      }

      if (Helpers.customFilterList && Helpers.customFilterList.length) {
        initialQueryParams.hierarchy_stack = [];
        Helpers.customFilterList.forEach(function (filter) {
          initialQueryParams.hierarchy_stack.push({
            "field_id": filter.field,
            "group_key": [filter.value]
          })
        });
      }
    }

    return initialQueryParams;
  }
}
